import { useState, useRef, useEffect, ChangeEvent } from 'react';

import { DossierId, Memo } from '../../../../resources/dossier/types';
import { useChangeMemoMutation } from '../../../../resources/dossier/mutations';
import { debounce } from '../../../../resources/pipeline/utils';
import { updateMemoInCache } from '../../../../resources/dossier/cache-utils';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

import { extractPlainTextFromHtml } from './MemoEditableItem';

type UseMemoEditorOptions = {
    dossierId: DossierId;
    memo: Memo;
};

export function useMemoEditor({ dossierId, memo }: UseMemoEditorOptions) {
    const plainTextMemo = extractPlainTextFromHtml(memo);
    const [localValue, setLocalValue] = useState<string>(plainTextMemo);
    const changeMemoMutation = useChangeMemoMutation(dossierId);
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const lastSavedValueRef = useRef<string>(plainTextMemo);
    const isUserEditingRef = useRef<boolean>(false);

    /* eslint-disable react-hooks/refs */
    const debouncedSave = useRef(
        debounce(1000, (value: string, originalValue: string) => {
            if (value.trim() !== originalValue) {
                changeMemoMutation.mutate({ memo: value.trim() || null });
                trackEvent((ampli, defaultProperties) => {
                    ampli.memoSaved({
                        ...defaultProperties,
                        ...trackedTripProperties,
                    });
                });
                lastSavedValueRef.current = value.trim();
            }
        }),
    ).current;
    /* eslint-enable react-hooks/refs */

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [debouncedSave]);

    useEffect(() => {
        const plainText = extractPlainTextFromHtml(memo);
        if (!isUserEditingRef.current && plainText.trim() !== lastSavedValueRef.current) {
            setLocalValue(plainText);
            lastSavedValueRef.current = plainText;
        }
    }, [memo]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        const trimmedValue = newValue.trim() || null;

        setLocalValue(newValue);
        updateMemoInCache(dossierId, trimmedValue);
        debouncedSave(newValue, lastSavedValueRef.current);
    };

    const handleFocus = () => {
        isUserEditingRef.current = true;
    };

    const handleBlur = () => {
        isUserEditingRef.current = false;
    };

    return {
        localValue,
        handleChange,
        handleFocus,
        handleBlur,
        isUserEditingRef,
        lastSavedValueRef,
        changeMemoMutation,
    };
}
